/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }

      // 绑定带有 data-popup-trigger 属性的 announcement block 点击事件
      this.bindPopupTriggers();
    }

    /**
     * 为配置了"点击唤起留资弹窗"的 announcement block 绑定点击事件
     * 点击后派发 newsletter:open-popup 自定义事件，由 newsletter-popup 组件监听并弹出弹窗
     */
    bindPopupTriggers() {
      const triggers = this.querySelectorAll('[data-popup-trigger="true"]');
      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('newsletter:open-popup'));
        });
      });
    }

    /**
     * Start sliders a slidin'
     * 使用克隆首张 slide 的方式实现无缝循环轮播，
     * 避免从最后一张跳回第一张时滚动方向反转。
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        const slideCount = slides.length;
        slides[0].classList.add('is-visible');

        // 当有多张 slide 时，克隆第一张追加到末尾，实现无缝循环
        if (slideCount > 1) {
          const firstClone = slides[0].cloneNode(true);
          firstClone.classList.remove('is-visible');
          firstClone.setAttribute('aria-hidden', 'true');
          this.slider.appendChild(firstClone);

          // 为克隆的 slide 绑定弹窗触发事件（如有配置）
          if (firstClone.hasAttribute('data-popup-trigger')) {
            firstClone.addEventListener('click', (e) => {
              e.preventDefault();
              document.dispatchEvent(new CustomEvent('newsletter:open-popup'));
            });
          }
        }

        // 包含克隆 slide 在内的所有 slide
        const allSlides = this.slider.querySelectorAll('.announcement__text');
        let currentIndex = 0;

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const nextIndex = currentIndex + 1;

            // 滚动到下一张 slide
            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextIndex * allSlides[nextIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(allSlides[nextIndex].offsetLeft - 32, 0);
            }

            allSlides[nextIndex].classList.add('is-visible');
            allSlides[currentIndex].classList.remove('is-visible');
            currentIndex = nextIndex;

            // 如果刚滚到的是克隆的首张 slide，等平滑动画结束后瞬间跳回真正的第一张
            if (currentIndex >= slideCount) {
              setTimeout(() => {
                // 临时关闭平滑滚动，实现无感跳转
                this.slider.style.scrollBehavior = 'auto';
                if (document.dir === 'rtl') {
                  this.slider.scrollTo(0, 0);
                } else {
                  this.slider.scrollTo(allSlides[0].offsetLeft - 32, 0);
                }
                // 恢复平滑滚动
                this.slider.style.scrollBehavior = '';

                // 切换 is-visible 标记
                allSlides[0].classList.add('is-visible');
                allSlides[currentIndex].classList.remove('is-visible');
                currentIndex = 0;
              }, 500);
            }
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
